/**
 * Licensed Materials - Property of IBM
 * IBM Business Analytics (C) Copyright IBM Corp. 2019
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

define([
	'./Events',
	'underscore'
], (Events, _) => {
	return class EventEmitter {

		constructor(options, transaction) {
			this.events = options.events || new Events();
			this.events.setTransaction(transaction);
			this.name = options.name;
			this.isDisableTriggerOnSelf = true;
			this.enableEventPropagation(options);
		}

		enableTriggerOnSelf() {
			this.isDisableTriggerOnSelf = false;
		}

		enableEventPropagation(propagationInfo) {
			this.info = propagationInfo.info;
			this.namespace = propagationInfo.namespace;
			this.callStack = propagationInfo.callStack;
			this.parentEventEmitter = propagationInfo.parentEventEmitter;
		}

		on() {
			return this.events.on.apply(this.events, arguments);
		}

		off() {
			return this.events.off.apply(this.events, arguments);
		}


		deepFreeze(object) {
			if(!Object.isFrozen(object)){
				const propNames = Object.getOwnPropertyNames(object);
				for (let name of propNames) {
					let value = object[name];
					object[name] = value && typeof value === 'object' ?
						this.deepFreeze(value) : value;
				}
				object =  Object.freeze(object);
			}
			return object;
		}

		/**
		 *
		 * @param {Object} event
		 * @param {String} name  Event type (e.g. format)
		 * @param {String} type  Event name (e.g. change, add, remove) - If not provided the default will be "change"
		 * @param {Object} event.transactionToken
		 * @param {Object} event.info Object that contain info specific to the event
		 * @param {Object} event.tracking
		 * @param {Object} event.tracking.action
		 * @param {Object} event.tracking.action.name action name
		 * @param {Object[]} event.tracking.action.params action params
		 * @param {Object[]} event.tracking.undoActions
		 * @param {Object} event.tracking.undoAction[].name revers actino ion name
		 * @param  Object[]} event.tracking.undoAction[].params reverse action params
		 */
		trigger(event, isBubbling) {

			// We clone the event and freeze so that no one can modifu the event payload.
			// we used the flag isBubbling to indicate that it was alrady cloned
			// so that we don't clone again as we bubble the event up (for performance)
			if (!isBubbling) {
				event = JSON.parse(JSON.stringify(event));
			}

			const transactionToken = event.transactionToken;
			const bubbleInfo = event.bubbleInfo;

			let callStack = bubbleInfo ? bubbleInfo.callStack : null;
			if (!this.isDisableTriggerOnSelf) {
				// the event might be a frozen object.. so we start with a shallow clone.
				const trackingCopy = _.extend({}, event.tracking);
				trackingCopy.callStack = callStack ? callStack.concat() : [];

				const eventName = (event.type || 'change') + ':' + event.name;
				const payload = this.deepFreeze({
					name: eventName,
					info:  event.info || {},
					tracking: trackingCopy,
					transactionToken: transactionToken,
					context: event.context || {}
				});
				this.events.trigger(eventName, payload);
			}


			if (this.parentEventEmitter) {
				if (!callStack) {
					callStack = [];
				}
				callStack.unshift(this.callStack);

				const newName = this.namespace ? (this.namespace + ':' + event.name) : event.name;
				const bubbbleEventInfo = {
					name: newName,
					info: _.extend(_.extend({}, this.info), event.info),
					type: event.type,
					tracking: event.tracking,
					transactionToken: event.transactionToken,
					bubbleInfo: {
						callStack: callStack
					},
					context: _.extend({}, event.context)
				};
				this.parentEventEmitter.trigger(bubbbleEventInfo, true);
			}
		}
	};
});
